// Shared API error-handling helpers.
//
// Goals (additive / defensive only — does not change business logic):
//  - Normalize Supabase PostgrestError / FunctionsError / Error / string into a
//    friendly, user-facing message via getErrorMessage().
//  - Wrap supabase.functions.invoke so the "200-with-error" case is caught:
//    an edge function can return HTTP 200 with { error } / { success: false }
//    in the body. invokeFn() treats both the transport `error` AND a failing
//    body as a failure, with an optional AbortController timeout.
//
// Usage:
//   import { getErrorMessage, invokeFn } from '@/lib/apiError';
//   try {
//     const data = await invokeFn('my-fn', { ...body });
//     // only reached on a REAL success
//   } catch (e) {
//     toast.error(getErrorMessage(e));
//   }
import { supabase } from '@/integrations/supabase/client';

/** Shape of a Supabase PostgrestError (kept loose to avoid type coupling). */
interface PostgrestLikeError {
  message?: string;
  details?: string;
  hint?: string;
  code?: string;
}

/**
 * Normalize any thrown/returned error into a single friendly string.
 * Handles: Error, Supabase PostgrestError/FunctionsError, plain strings,
 * objects with { error } / { message }, and unknown values.
 */
export function getErrorMessage(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (e == null) return fallback;

  if (typeof e === 'string') return e.trim() || fallback;

  if (e instanceof Error) {
    // FunctionsError often carries a richer body on .context
    const ctx = (e as { context?: unknown }).context;
    const fromCtx = extractFromContext(ctx);
    if (fromCtx) return fromCtx;
    return e.message?.trim() || fallback;
  }

  if (typeof e === 'object') {
    const obj = e as Record<string, unknown>;

    // Nested { error: ... }
    if (obj.error != null && obj.error !== e) {
      const nested = getErrorMessage(obj.error, '');
      if (nested) return nested;
    }

    const pg = e as PostgrestLikeError;
    if (typeof pg.message === 'string' && pg.message.trim()) {
      // Surface useful detail/hint when present, but keep it short.
      const detail = typeof pg.details === 'string' && pg.details.trim() ? ` (${pg.details.trim()})` : '';
      return `${pg.message.trim()}${detail}`;
    }

    if (typeof obj.msg === 'string' && obj.msg.trim()) return obj.msg.trim();
  }

  return fallback;
}

/** Pull a message out of a FunctionsError-style `context` (which may hold the body). */
function extractFromContext(ctx: unknown): string | null {
  if (ctx == null || typeof ctx !== 'object') return null;
  const body = (ctx as { body?: unknown }).body;
  if (body == null) return null;
  try {
    const parsed = typeof body === 'string' ? JSON.parse(body) : body;
    if (parsed && typeof parsed === 'object') {
      const p = parsed as Record<string, unknown>;
      if (typeof p.error === 'string' && p.error.trim()) return p.error.trim();
      if (typeof p.message === 'string' && p.message.trim()) return p.message.trim();
    }
  } catch {
    // body wasn't JSON — ignore
  }
  return null;
}

/**
 * True when a 200-OK function body actually represents a failure.
 * Catches { error: ... } and explicit { success: false }.
 */
function bodyIndicatesFailure(data: unknown): boolean {
  if (data == null || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  if (obj.error != null && obj.error !== '') return true;
  if (obj.success === false) return true;
  return false;
}

export interface InvokeOptions {
  /** Timeout in ms; rejects with a friendly error if the function hangs. Default 20s. */
  timeoutMs?: number;
  /**
   * When true, a body with { success: false } / { error } is treated as a failure
   * and throws. Default true. Set false for callers that want to inspect the raw body.
   */
  failOnErrorBody?: boolean;
  /**
   * Extra headers merged into the request - in particular, an explicit
   * Authorization override. supabase-js normally attaches the current session's
   * access token to functions.invoke() automatically, but the very first
   * authenticated call right after sign-in can race the SDK's internal session
   * plumbing (the session exists, but hasn't been wired into the client's
   * auto-attach path yet), producing a spurious 401. Pass an explicit
   * Authorization header (from a freshly-fetched supabase.auth.getSession())
   * to bypass that race entirely for calls known to run early in a session.
   */
  headers?: Record<string, string>;
}

/**
 * Wrap supabase.functions.invoke with robust failure handling:
 *  - surfaces the transport `error` (non-2xx),
 *  - treats a 200-with-error body ({ error } / { success:false }) as a failure,
 *  - applies an AbortController timeout so a hung request can't wedge the UI.
 *
 * Returns the function body on REAL success; throws an Error otherwise.
 * The thrown Error is safe to pass straight to getErrorMessage().
 */
export async function invokeFn<T = unknown>(
  name: string,
  body?: unknown,
  options: InvokeOptions = {}
): Promise<T> {
  const { timeoutMs = 20000, failOnErrorBody = true, headers } = options;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const { data, error } = await supabase.functions.invoke(name, {
      body: body as Record<string, unknown> | undefined,
      signal: controller.signal,
      ...(headers ? { headers } : {}),
    });

    // Transport-level error (non-2xx). supabase-js throws BEFORE parsing the
    // response body on any non-2xx status, so `data` here is always null and
    // `error.context` is the raw, UNREAD Response object - `.context.body` is a
    // ReadableStream, never a pre-parsed string/object. Must await `.text()` to
    // get the function's real error reason instead of the generic
    // "Edge Function returned a non-2xx status code".
    if (error) {
      const ctx = (error as { context?: Response }).context;
      let detail = '';
      if (ctx && typeof ctx.text === 'function') {
        try {
          const text = await ctx.text();
          if (text) {
            try {
              const parsed = JSON.parse(text);
              detail = (parsed?.error || parsed?.message || '').toString().trim();
            } catch {
              detail = text.trim();
            }
          }
        } catch {
          // body already consumed or unreadable - fall through to the generic message
        }
      }
      const status = ctx?.status;
      const authHint = status === 401 ? ' Your session may have expired - try refreshing the page.' : '';
      throw new Error((detail || getErrorMessage(error, `Request to "${name}" failed`)) + authHint);
    }

    // 200-with-error body case.
    if (failOnErrorBody && bodyIndicatesFailure(data)) {
      throw new Error(getErrorMessage(data, `"${name}" reported a failure`));
    }

    return data as T;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new Error(`Request timed out. Please check your connection and try again.`);
    }
    // Re-throw normalized so callers get a clean Error.
    if (e instanceof Error) throw e;
    throw new Error(getErrorMessage(e, `Request to "${name}" failed`));
  } finally {
    clearTimeout(timer);
  }
}

export interface FetchJsonOptions extends RequestInit {
  /** Timeout in ms; aborts a hung request. Default 20s. */
  timeoutMs?: number;
}

/**
 * fetch() wrapper that handles: network rejection, non-2xx status, empty/invalid
 * JSON, and a hung request (AbortController timeout). Returns parsed JSON on
 * success; throws a friendly Error otherwise.
 */
export async function fetchJson<T = unknown>(
  input: RequestInfo | URL,
  options: FetchJsonOptions = {}
): Promise<T> {
  const { timeoutMs = 20000, ...init } = options;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let res: Response;
  try {
    res = await fetch(input, { ...init, signal: controller.signal });
  } catch (e) {
    clearTimeout(timer);
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new Error('Request timed out. Please check your connection and try again.');
    }
    throw new Error(getErrorMessage(e, 'Network request failed. Please check your connection.'));
  }
  clearTimeout(timer);

  const text = await res.text().catch(() => '');
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = undefined;
    }
  }

  if (!res.ok) {
    const msg = getErrorMessage(parsed, '') || `Request failed (${res.status} ${res.statusText})`;
    throw new Error(msg);
  }

  return parsed as T;
}
